import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { MobileAuthService } from '../../../core/services/mobile-auth.service';
import { TranslationService } from '../../../core/services/translation.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule],
})
export class LoginComponent implements OnInit, OnDestroy {
  @Output() loginSuccess = new EventEmitter<{
    email: string;
    name: string;
    roles?: string[];
    permissions?: string[];
  }>();
  @Output() registerSuccess = new EventEmitter<{
    email: string;
    name: string;
    roles?: string[];
    permissions?: string[];
  }>();

  private auth0 = inject(Auth0Service);
  private mobileAuth = inject(MobileAuthService);
  private translationService = inject(TranslationService);

  showRegister = false;
  isLoading = false;
  private urlOpenListener: any;
  private auth0ListenerHandle: any;
  private pendingAuth0State: string | null = null;
  private auth0Resolve: ((result: any) => void) | null = null;
  private auth0Reject: ((error: any) => void) | null = null;
  private translationSubscription?: Subscription;

  // Check if running on mobile
  isMobile = Capacitor.isNativePlatform();

  // Dynamic logo URL based on language
  logoUrl: string =
    'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/TilHib_Logo.jpg?alt=media&token=94d9319d-107c-43bc-9a40-ec503096b61b';

  ngOnInit() {
    // Subscribe to language changes to update logo
    this.translationSubscription =
      this.translationService.currentLanguage$.subscribe((language) => {
        this.updateLogoUrl(language);
      });
    // Initialize logo with current language
    this.updateLogoUrl(this.translationService.getCurrentLanguage());

    // Listen for Auth0 authentication status (WEB ONLY)
    if (!Capacitor.isNativePlatform()) {
      this.auth0.isAuthenticated$.subscribe((isAuth) => {
        if (isAuth) {
          // User is authenticated, get their info
          this.auth0.user$.subscribe((auth0User) => {
            if (auth0User) {
              console.log('✅ Web Auth0 authenticated, emitting loginSuccess');
              this.isLoading = false;

              // Extract roles and permissions from Auth0 user
              const roles =
                auth0User['https://kyrgyz-translation-api/roles'] || [];
              const permissions =
                auth0User['https://kyrgyz-translation-api/permissions'] || [];

              // Emit login success to show main page
              this.loginSuccess.emit({
                email: auth0User.email || '',
                name: auth0User.name || auth0User.email || 'User',
                roles: roles,
                permissions: permissions,
              });
            }
          });
        }
      });
    } else {
      // Set up app state listeners for better callback handling (MOBILE ONLY)
      this.mobileAuth.setupAppStateListeners();

      // Set up Auth0 deep link listener once and wait for it
      this.setupAuth0Listener()
        .then(() => {
          // Auth0 listener setup complete
        })
        .catch((error) => {
          console.error('❌ Error setting up Auth0 listener:', error);
        });

      // Check for tokens from web callback when component loads (MOBILE ONLY)
      const callbackResult = this.mobileAuth.checkForWebCallbackTokens();
      if (callbackResult && callbackResult.code) {
        this.mobileAuth
          .getUserInfo(callbackResult.code)
          .then((userInfo) => {
            // Extract roles and permissions from Auth0 user info
            const roles =
              userInfo['https://kyrgyz-translation-api/roles'] || [];
            const permissions =
              userInfo['https://kyrgyz-translation-api/permissions'] || [];

            this.loginSuccess.emit({
              email: userInfo.email || '',
              name: userInfo.name || userInfo.email || 'User',
              roles: roles,
              permissions: permissions,
            });
          })
          .catch((error) => {
            console.error('❌ Error getting user info from callback:', error);
          });
      }

      // Set up periodic check for tokens in case callback was missed
      const tokenCheckInterval = setInterval(() => {
        const hasAccessToken = !!localStorage.getItem('auth0_access_token');
        const hasIdToken = !!localStorage.getItem('auth0_id_token');

        if (hasAccessToken && hasIdToken && this.isLoading) {
          console.log('✅ Found tokens during periodic check, processing...');
          const accessToken = localStorage.getItem('auth0_access_token');
          if (accessToken) {
            this.mobileAuth
              .getUserInfo(accessToken)
              .then((userInfo) => {
                this.isLoading = false;

                // Extract roles and permissions from Auth0 user info
                const roles =
                  userInfo['https://kyrgyz-translation-api/roles'] || [];
                const permissions =
                  userInfo['https://kyrgyz-translation-api/permissions'] || [];

                this.loginSuccess.emit({
                  email: userInfo.email || '',
                  name: userInfo.name || userInfo.email || 'User',
                  roles: roles,
                  permissions: permissions,
                });
                clearInterval(tokenCheckInterval);
              })
              .catch((error) => {
                console.error(
                  '❌ Error getting user info from periodic check:',
                  error
                );
              });
          }
        }
      }, 2000); // Check every 2 seconds

      // Clear interval after 5 minutes to prevent memory leaks
      setTimeout(() => {
        clearInterval(tokenCheckInterval);
      }, 300000);
    }
  }

  ngOnDestroy() {
    // Clean up Auth0 listener
    if (this.auth0ListenerHandle) {
      this.auth0ListenerHandle.remove();
    }
    // Clean up translation subscription
    if (this.translationSubscription) {
      this.translationSubscription.unsubscribe();
    }
  }

  /**
   * Update logo URL based on current language setting
   * Maps language codes to appropriate logo images
   */
  private updateLogoUrl(language: string): void {
    // Map of language codes to logo URLs
    // Login page uses TilHib_Logo.jpg for all languages
    const logoMap: { [key: string]: string } = {
      en: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/TilHib_Logo.jpg?alt=media&token=94d9319d-107c-43bc-9a40-ec503096b61b',
      ko: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/TilHib_Logo.jpg?alt=media&token=94d9319d-107c-43bc-9a40-ec503096b61b',
      'zh-CN':
        'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/TilHib_Logo.jpg?alt=media&token=94d9319d-107c-43bc-9a40-ec503096b61b',
      'zh-HK':
        'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/TilHib_Logo.jpg?alt=media&token=94d9319d-107c-43bc-9a40-ec503096b61b',
      zh: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/TilHib_Logo.jpg?alt=media&token=94d9319d-107c-43bc-9a40-ec503096b61b',
      ky: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/TilHib_Logo.jpg?alt=media&token=94d9319d-107c-43bc-9a40-ec503096b61b',
      ru: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/TilHib_Logo.jpg?alt=media&token=94d9319d-107c-43bc-9a40-ec503096b61b',
      kk: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/TilHib_Logo.jpg?alt=media&token=94d9319d-107c-43bc-9a40-ec503096b61b',
      tg: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/TilHib_Logo.jpg?alt=media&token=94d9319d-107c-43bc-9a40-ec503096b61b',
      tk: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/TilHib_Logo.jpg?alt=media&token=94d9319d-107c-43bc-9a40-ec503096b61b',
      uz: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/TilHib_Logo.jpg?alt=media&token=94d9319d-107c-43bc-9a40-ec503096b61b',
      fa: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/TilHib_Logo.jpg?alt=media&token=94d9319d-107c-43bc-9a40-ec503096b61b',
      es: 'https://firebasestorage.googleapis.com/v0/b/translation-5c1b8.firebasestorage.app/o/TilHib_Logo.jpg?alt=media&token=94d9319d-107c-43bc-9a40-ec503096b61b',
    };

    // Get logo URL for current language, fallback to default
    this.logoUrl = logoMap[language] || logoMap['en'];
  }

  // Set up Auth0 deep link listener once
  async setupAuth0Listener() {
    if (this.auth0ListenerHandle) {
      return; // Already set up
    }

    // Setting up Auth0 deep link listener
    this.auth0ListenerHandle = await App.addListener(
      'appUrlOpen',
      async (event: URLOpenListenerEvent) => {
        // Deep link received
        console.log('📱 Deep link received:', event.url);

        if (event.url.includes('com.kyrgz.translation')) {
          console.log('✅ Auth0 callback detected, closing browser');
          console.log('🔍 Full callback URL:', event.url);
          console.log(
            '🔍 Source application:',
            (event as any).iosSourceApplication || 'unknown'
          );
          Browser.close().catch(() => {}); // Ignore errors if already closed

          // Parse the callback URL to get tokens
          const url = new URL(event.url);
          const hash = url.hash.substring(1); // Remove the #
          const params = new URLSearchParams(hash);

          const accessToken = params.get('access_token');
          const idToken = params.get('id_token');
          const error = params.get('error');
          const returnedState = params.get('state');

          console.log('🔍 Callback state:', returnedState);
          console.log('🔍 Expected state:', this.pendingAuth0State);
          console.log('🔍 Access token present:', !!accessToken);
          console.log('🔍 ID token present:', !!idToken);
          console.log('🔍 Error present:', !!error);

          // Decode URL-encoded tokens (URLSearchParams doesn't auto-decode)
          const decodedAccessToken = accessToken
            ? decodeURIComponent(accessToken)
            : null;
          const decodedIdToken = idToken ? decodeURIComponent(idToken) : null;

          if (error) {
            console.error('❌ Auth0 error:', error);
            console.error('❌ Auth0 error details:', {
              error,
              returnedState,
              expectedState: this.pendingAuth0State,
            });
            this.isLoading = false;
            if (this.auth0Reject) {
              this.auth0Reject(new Error(error));
              this.auth0Reject = null;
              this.auth0Resolve = null;
            }

            // Provide more specific error messages for Facebook login
            let errorMessage = 'Authentication failed. ';
            if (error === 'access_denied') {
              errorMessage +=
                'Facebook login was denied. Please try again or use a different login method.';
            } else if (error === 'consent_required') {
              errorMessage +=
                'Facebook requires additional permissions. Please try again.';
            } else if (error === 'login_required') {
              errorMessage += 'Please try logging in again.';
            } else {
              errorMessage += `Error: ${error}`;
            }

            alert(errorMessage);
          } else if (decodedAccessToken && decodedIdToken) {
            // For iOS SafariViewService, we'll be more lenient with state validation
            // as the state might not be preserved correctly in some cases
            if (returnedState !== this.pendingAuth0State) {
              console.warn(
                '⚠️ State mismatch detected, but proceeding with valid tokens'
              );
              console.warn('⚠️ Returned state:', returnedState);
              console.warn('⚠️ Expected state:', this.pendingAuth0State);
            }
            console.log(
              '✅ Valid tokens received, storing and getting user info'
            );
            // Store decoded tokens
            localStorage.setItem('auth0_access_token', decodedAccessToken);
            localStorage.setItem('auth0_id_token', decodedIdToken);

            // Get user info and complete login/registration
            try {
              const userInfo = await this.mobileAuth.getUserInfo(
                decodedAccessToken
              );
              console.log('✅ User info received:', userInfo);
              console.log('📧 User email:', userInfo.email);
              console.log('👤 User sub:', userInfo.sub);
              console.log('🔍 User name:', userInfo.name);

              // For Facebook users without email, use the Auth0 sub as identifier
              const userEmail = userInfo.email || userInfo.sub || '';
              const userName =
                userInfo.name ||
                userInfo.given_name ||
                userInfo.nickname ||
                'User';

              console.log('📧 Final user email/identifier:', userEmail);
              console.log('👤 Final user name:', userName);

              this.isLoading = false;

              // Extract roles and permissions from Auth0 user info
              const roles =
                userInfo['https://kyrgyz-translation-api/roles'] || [];
              const permissions =
                userInfo['https://kyrgyz-translation-api/permissions'] || [];

              console.log('🔍 Extracted roles:', roles);
              console.log('🔍 Extracted permissions:', permissions);

              // Resolve the Promise with user info including roles and permissions
              if (this.auth0Resolve) {
                this.auth0Resolve({
                  email: userEmail,
                  name: userName,
                  roles: roles,
                  permissions: permissions,
                });
                // Clear callbacks after successful resolution
                this.auth0Reject = null;
                this.auth0Resolve = null;
                this.pendingAuth0State = null;
              }
            } catch (userError) {
              console.error('❌ Error getting user info:', userError);
              this.isLoading = false;
              if (this.auth0Reject) {
                this.auth0Reject(userError);
                this.auth0Reject = null;
                this.auth0Resolve = null;
              }
            }
          } else {
            console.error(
              '❌ Invalid Auth0 response - missing tokens or other issue'
            );
            console.error('❌ Debug info:', {
              hasAccessToken: !!decodedAccessToken,
              hasIdToken: !!decodedIdToken,
              returnedState,
              expectedState: this.pendingAuth0State,
              url: event.url,
            });
            this.isLoading = false;
            if (this.auth0Reject) {
              this.auth0Reject(
                new Error('Invalid authentication response - missing tokens')
              );
              this.auth0Reject = null;
              this.auth0Resolve = null;
            }
            alert(
              '❌ Authentication failed - missing tokens. Please try again.'
            );
          }
        }
      }
    );
    console.log('✅ Auth0 listener set up successfully');
  }

  // Handle Auth0 login click with proper event handling
  async handleAuth0LoginClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    try {
      await this.loginWithAuth0();
    } catch (error) {
      console.error('❌ Auth0 login failed:', error);
      this.isLoading = false;
      alert('Auth0 login failed: ' + (error as Error).message);
    }
  }

  // Handle Auth0 registration click with proper event handling
  async handleAuth0RegisterClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    try {
      await this.registerWithAuth0();
    } catch (error) {
      console.error('❌ Auth0 registration failed:', error);
      this.isLoading = false;
      alert('Auth0 registration failed: ' + (error as Error).message);
    }
  }

  // Handle Sign in with Apple click
  async handleAppleLoginClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    try {
      await this.loginWithApple();
    } catch (error) {
      console.error('❌ Apple login failed:', error);
      this.isLoading = false;
      alert('Apple login failed: ' + (error as Error).message);
    }
  }

  // Auth0 Login - wait for deep link success
  async loginWithAuth0(): Promise<void> {
    console.log('🔐 Auth0 login button clicked');
    this.isLoading = true;

    // Set a timeout to prevent infinite loading
    // Increased to 120 seconds for first-time social logins (requires approval)
    const loadingTimeout = setTimeout(async () => {
      if (this.isLoading && this.auth0Reject) {
        console.log(
          '⚠️ Auth0 login timeout, checking if login actually succeeded...'
        );

        // Check if we have tokens in localStorage (login might have succeeded)
        const accessToken = localStorage.getItem('auth0_access_token');
        const idToken = localStorage.getItem('auth0_id_token');

        if (accessToken && idToken) {
          console.log(
            '✅ Login actually succeeded - tokens found, completing login...'
          );

          try {
            // Complete the login process by getting user info
            const userInfo = await this.mobileAuth.getUserInfo(accessToken);
            console.log('✅ User info received from tokens:', userInfo);

            // Extract user email and name
            const userEmail = userInfo.email || userInfo.sub || '';
            const userName =
              userInfo.name ||
              userInfo.given_name ||
              userInfo.nickname ||
              'User';

            // Extract roles and permissions from Auth0 user info
            const roles =
              userInfo['https://kyrgyz-translation-api/roles'] || [];
            const permissions =
              userInfo['https://kyrgyz-translation-api/permissions'] || [];

            this.isLoading = false;

            // Complete the login by resolving with user info
            if (this.auth0Resolve) {
              this.auth0Resolve({
                email: userEmail,
                name: userName,
                roles: roles,
                permissions: permissions,
              });
            }

            // Clear callbacks after successful resolution
            this.auth0Reject = null;
            this.auth0Resolve = null;
            this.pendingAuth0State = null;
          } catch (error) {
            console.error('❌ Error completing login from tokens:', error);
            // If we can't get user info, still clear loading but show error
            this.isLoading = false;
            if (this.auth0Reject) {
              this.auth0Reject(
                new Error(
                  'Failed to complete login: ' + (error as Error).message
                )
              );
            }
            this.auth0Reject = null;
            this.auth0Resolve = null;
            this.pendingAuth0State = null;
          }
        } else {
          console.log('❌ Login genuinely timed out - no tokens found');
          this.isLoading = false;
          if (this.auth0Reject) {
            this.auth0Reject(new Error('Auth0 login timed out'));
          }
          this.auth0Reject = null;
          this.auth0Resolve = null;
          this.pendingAuth0State = null;
        }
      }
    }, 120000); // Increased to 120 seconds to allow more time for first-time social logins

    try {
      if (Capacitor.isNativePlatform()) {
        // Mobile: Use Promise-based flow that waits for deep link success
        console.log(
          '📱 Using mobile Auth0 flow with Promise-based deep link waiting'
        );

        // Ensure listener is set up
        if (!this.auth0ListenerHandle) {
          console.log('⚠️ Auth0 listener not ready, setting up now...');
          await this.setupAuth0Listener();
        }

        // Generate random state and nonce for security
        const state = Math.random().toString(36).substring(7);
        const nonce = Math.random().toString(36).substring(7);

        // Store state for validation
        this.pendingAuth0State = state;

        // Get Auth0 config from environment
        const auth0Domain =
          environment.auth0?.domain || 'dev-2hjdm0qn6otliqls.us.auth0.com';
        const auth0ClientId =
          environment.auth0?.clientId ||
          (environment.production
            ? 'Xg3Gxsm7YIddHVkM1yohO76kADkfN4Qa'
            : 'RM8No7FzHr5jp90nhTsviuiLRmlCLLYP');

        // Auth0 URL with proper deep link handling
        const authUrl =
          `https://${auth0Domain}/authorize?` +
          `client_id=${auth0ClientId}&` +
          'redirect_uri=com.kyrgz.translation%3A%2F%2Fcallback&' +
          'response_type=token%20id_token&' +
          'scope=openid%20profile%20email&' +
          'state=' +
          state +
          '&' +
          'nonce=' +
          nonce +
          '&' +
          'prompt=select_account&' +
          'max_age=0';

        console.log('🔗 Auth0 Login URL generated:', authUrl);
        console.log('🔍 Auth0 URL parameters:', {
          client_id: auth0ClientId,
          redirect_uri: 'com.kyrgz.translation://callback',
          response_type: 'token id_token',
          scope: 'openid profile email',
          state: state,
          nonce: nonce,
          prompt: 'select_account',
          max_age: '0',
        });

        // Return a Promise that resolves when deep link succeeds
        return new Promise((resolve, reject) => {
          this.auth0Resolve = (userInfo) => {
            clearTimeout(loadingTimeout);
            // Auth0 login successful
            this.loginSuccess.emit(userInfo);
            resolve();
          };

          this.auth0Reject = (error) => {
            clearTimeout(loadingTimeout);
            console.error('❌ Auth0 login failed:', error);
            reject(error);
          };

          // Open Auth0 in browser
          Browser.open({ url: authUrl })
            .then(() => {
              // Auth0 browser opened
              console.log(
                '🌐 Browser opened, starting periodic token check...'
              );

              // Start periodic checks for tokens (in case callback is delayed)
              // This helps catch first-time social logins that take longer
              const tokenCheckInterval = setInterval(async () => {
                // Only check if we're still loading and haven't resolved yet
                if (!this.isLoading || !this.auth0Resolve) {
                  clearInterval(tokenCheckInterval);
                  return;
                }

                const accessToken = localStorage.getItem('auth0_access_token');
                const idToken = localStorage.getItem('auth0_id_token');

                if (accessToken && idToken) {
                  console.log(
                    '✅ Tokens found via periodic check, completing login...'
                  );
                  clearInterval(tokenCheckInterval);
                  clearTimeout(loadingTimeout);

                  try {
                    // Complete the login process
                    const userInfo = await this.mobileAuth.getUserInfo(
                      accessToken
                    );
                    const userEmail = userInfo.email || userInfo.sub || '';
                    const userName =
                      userInfo.name ||
                      userInfo.given_name ||
                      userInfo.nickname ||
                      'User';
                    const roles =
                      userInfo['https://kyrgyz-translation-api/roles'] || [];
                    const permissions =
                      userInfo['https://kyrgyz-translation-api/permissions'] ||
                      [];

                    this.isLoading = false;

                    if (this.auth0Resolve) {
                      this.auth0Resolve({
                        email: userEmail,
                        name: userName,
                        roles: roles,
                        permissions: permissions,
                      });
                    }

                    this.auth0Reject = null;
                    this.auth0Resolve = null;
                    this.pendingAuth0State = null;
                  } catch (error) {
                    console.error(
                      '❌ Error completing login from periodic check:',
                      error
                    );
                    clearInterval(tokenCheckInterval);
                  }
                }
              }, 2000); // Check every 2 seconds

              // Clear interval when timeout fires
              setTimeout(() => {
                clearInterval(tokenCheckInterval);
              }, 120000);
            })
            .catch((browserError) => {
              clearTimeout(loadingTimeout);
              this.isLoading = false;
              reject(browserError);
            });
        });
      } else {
        // Web: Use standard Auth0 Angular SDK
        console.log('🌐 Using web Auth0 flow');
        this.auth0.loginWithRedirect({
          appState: { target: '/' },
        });
        clearTimeout(loadingTimeout);
      }
    } catch (error) {
      console.error('❌ Auth0 login error:', error);
      clearTimeout(loadingTimeout);
      this.isLoading = false;
      alert('Auth0 login failed: ' + (error as Error).message);
      throw error;
    }
  }

  // Sign in with Apple - direct connection
  async loginWithApple(): Promise<void> {
    console.log('🍎 Sign in with Apple button clicked');
    this.isLoading = true;

    // Set a timeout to prevent infinite loading
    const loadingTimeout = setTimeout(async () => {
      if (this.isLoading && this.auth0Reject) {
        console.log(
          '⚠️ Apple login timeout, checking if login actually succeeded...'
        );

        const accessToken = localStorage.getItem('auth0_access_token');
        const idToken = localStorage.getItem('auth0_id_token');

        if (accessToken && idToken) {
          console.log(
            '✅ Login actually succeeded - tokens found, completing login...'
          );

          try {
            const userInfo = await this.mobileAuth.getUserInfo(accessToken);
            const userEmail = userInfo.email || userInfo.sub || '';
            const userName =
              userInfo.name ||
              userInfo.given_name ||
              userInfo.nickname ||
              'User';
            const roles =
              userInfo['https://kyrgyz-translation-api/roles'] || [];
            const permissions =
              userInfo['https://kyrgyz-translation-api/permissions'] || [];

            this.isLoading = false;

            if (this.auth0Resolve) {
              this.auth0Resolve({
                email: userEmail,
                name: userName,
                roles: roles,
                permissions: permissions,
              });
            }

            this.auth0Reject = null;
            this.auth0Resolve = null;
            this.pendingAuth0State = null;
          } catch (error) {
            console.error('❌ Error completing login from tokens:', error);
            this.isLoading = false;
            if (this.auth0Reject) {
              this.auth0Reject(
                new Error(
                  'Failed to complete login: ' + (error as Error).message
                )
              );
            }
            this.auth0Reject = null;
            this.auth0Resolve = null;
            this.pendingAuth0State = null;
          }
        } else {
          console.log('❌ Login genuinely timed out - no tokens found');
          this.isLoading = false;
          if (this.auth0Reject) {
            this.auth0Reject(new Error('Apple login timed out'));
          }
          this.auth0Reject = null;
          this.auth0Resolve = null;
          this.pendingAuth0State = null;
        }
      }
    }, 120000);

    try {
      if (Capacitor.isNativePlatform()) {
        // Mobile: Use Promise-based flow with Apple connection
        console.log('📱 Using mobile Apple login flow');

        if (!this.auth0ListenerHandle) {
          console.log('⚠️ Auth0 listener not ready, setting up now...');
          await this.setupAuth0Listener();
        }

        const state = Math.random().toString(36).substring(7);
        const nonce = Math.random().toString(36).substring(7);
        this.pendingAuth0State = state;

        const auth0Domain =
          environment.auth0?.domain || 'dev-2hjdm0qn6otliqls.us.auth0.com';
        const auth0ClientId =
          environment.auth0?.clientId ||
          (environment.production
            ? 'Xg3Gxsm7YIddHVkM1yohO76kADkfN4Qa'
            : 'RM8No7FzHr5jp90nhTsviuiLRmlCLLYP');

        // Auth0 URL with Apple connection parameter
        const authUrl =
          `https://${auth0Domain}/authorize?` +
          `client_id=${auth0ClientId}&` +
          'redirect_uri=com.kyrgz.translation%3A%2F%2Fcallback&' +
          'response_type=token%20id_token&' +
          'scope=openid%20profile%20email&' +
          `connection=apple&` + // Specify Apple connection
          `state=${state}&` +
          `nonce=${nonce}&` +
          'prompt=select_account&' +
          'max_age=0';

        console.log('🍎 Apple login URL:', authUrl);

        return new Promise((resolve, reject) => {
          this.auth0Resolve = (userInfo) => {
            clearTimeout(loadingTimeout);
            this.loginSuccess.emit(userInfo);
            resolve();
          };

          this.auth0Reject = (error) => {
            clearTimeout(loadingTimeout);
            console.error('❌ Apple login failed:', error);
            reject(error);
          };

          Browser.open({ url: authUrl })
            .then(() => {
              console.log('🌐 Browser opened for Apple login');
            })
            .catch((browserError) => {
              clearTimeout(loadingTimeout);
              this.isLoading = false;
              reject(browserError);
            });
        });
      } else {
        // Web: Use Auth0 with Apple connection
        console.log('🌐 Using web Apple login flow');
        this.auth0.loginWithRedirect({
          appState: { target: '/' },
          authorizationParams: {
            connection: 'apple',
          },
        });
        clearTimeout(loadingTimeout);
      }
    } catch (error) {
      console.error('❌ Apple login error:', error);
      clearTimeout(loadingTimeout);
      this.isLoading = false;
      alert('Apple login failed: ' + (error as Error).message);
      throw error;
    }
  }

  // Auth0 Registration - wait for deep link success
  async registerWithAuth0(): Promise<void> {
    console.log('📝 Auth0 register button clicked');
    this.isLoading = true;

    // Set a timeout to prevent infinite loading
    // Increased to 120 seconds for first-time social logins (requires approval)
    const loadingTimeout = setTimeout(async () => {
      if (this.isLoading && this.auth0Reject) {
        console.log(
          '⚠️ Auth0 registration timeout, checking if registration actually succeeded...'
        );

        // Check if we have tokens in localStorage (registration might have succeeded)
        const accessToken = localStorage.getItem('auth0_access_token');
        const idToken = localStorage.getItem('auth0_id_token');

        if (accessToken && idToken) {
          console.log(
            '✅ Registration actually succeeded - tokens found, completing registration...'
          );

          try {
            // Complete the registration process by getting user info
            const userInfo = await this.mobileAuth.getUserInfo(accessToken);
            console.log('✅ User info received from tokens:', userInfo);

            // Extract user email and name
            const userEmail = userInfo.email || userInfo.sub || '';
            const userName =
              userInfo.name ||
              userInfo.given_name ||
              userInfo.nickname ||
              'User';

            // Extract roles and permissions from Auth0 user info
            const roles =
              userInfo['https://kyrgyz-translation-api/roles'] || [];
            const permissions =
              userInfo['https://kyrgyz-translation-api/permissions'] || [];

            this.isLoading = false;

            // Complete the registration by resolving with user info
            if (this.auth0Resolve) {
              this.auth0Resolve({
                email: userEmail,
                name: userName,
                roles: roles,
                permissions: permissions,
              });
            }

            // Clear callbacks after successful resolution
            this.auth0Reject = null;
            this.auth0Resolve = null;
            this.pendingAuth0State = null;
          } catch (error) {
            console.error(
              '❌ Error completing registration from tokens:',
              error
            );
            // If we can't get user info, still clear loading but show error
            this.isLoading = false;
            if (this.auth0Reject) {
              this.auth0Reject(
                new Error(
                  'Failed to complete registration: ' + (error as Error).message
                )
              );
            }
            this.auth0Reject = null;
            this.auth0Resolve = null;
            this.pendingAuth0State = null;
          }
        } else {
          console.log('❌ Registration genuinely timed out - no tokens found');
          this.isLoading = false;
          if (this.auth0Reject) {
            this.auth0Reject(new Error('Auth0 registration timed out'));
          }
          this.auth0Reject = null;
          this.auth0Resolve = null;
          this.pendingAuth0State = null;
        }
      }
    }, 120000); // Increased to 120 seconds to allow more time for first-time social logins

    try {
      if (Capacitor.isNativePlatform()) {
        // Mobile: Use Promise-based flow that waits for deep link success
        console.log(
          '📱 Using mobile Auth0 registration flow with Promise-based deep link waiting'
        );

        // Ensure listener is set up
        if (!this.auth0ListenerHandle) {
          console.log('⚠️ Auth0 listener not ready, setting up now...');
          await this.setupAuth0Listener();
        }

        // Generate random state and nonce for security
        const state = Math.random().toString(36).substring(7);
        const nonce = Math.random().toString(36).substring(7);

        // Store state for validation
        this.pendingAuth0State = state;

        // Get Auth0 config from environment
        const auth0Domain =
          environment.auth0?.domain || 'dev-2hjdm0qn6otliqls.us.auth0.com';
        const auth0ClientId =
          environment.auth0?.clientId ||
          (environment.production
            ? 'Xg3Gxsm7YIddHVkM1yohO76kADkfN4Qa'
            : 'RM8No7FzHr5jp90nhTsviuiLRmlCLLYP');

        // Auth0 URL with proper deep link handling (Auth0 will show signup option)
        const authUrl =
          `https://${auth0Domain}/authorize?` +
          `client_id=${auth0ClientId}&` +
          'redirect_uri=com.kyrgz.translation%3A%2F%2Fcallback&' +
          'response_type=token%20id_token&' +
          'scope=openid%20profile%20email&' +
          'state=' +
          state +
          '&' +
          'nonce=' +
          nonce +
          '&' +
          'prompt=select_account&' +
          'max_age=0';

        console.log('🔗 Auth0 Registration URL:', authUrl);
        console.log('🔍 Auth0 Registration URL parameters:', {
          client_id: auth0ClientId,
          redirect_uri: 'com.kyrgz.translation://callback',
          response_type: 'token id_token',
          scope: 'openid profile email',
          state: state,
          nonce: nonce,
          prompt: 'select_account',
          max_age: '0',
        });

        // Return a Promise that resolves when deep link succeeds
        return new Promise((resolve, reject) => {
          this.auth0Resolve = (userInfo) => {
            clearTimeout(loadingTimeout);
            console.log(
              '✅ Auth0 registration successful, redirecting to main page'
            );
            this.loginSuccess.emit(userInfo); // Use loginSuccess for both login and registration
            resolve();
          };

          this.auth0Reject = (error) => {
            clearTimeout(loadingTimeout);
            console.error('❌ Auth0 registration failed:', error);
            reject(error);
          };

          // Open Auth0 in browser
          Browser.open({ url: authUrl })
            .then(() => {
              console.log(
                '✅ Auth0 registration browser opened, waiting for deep link...'
              );
              console.log(
                '🌐 Browser opened, starting periodic token check...'
              );

              // Start periodic checks for tokens (in case callback is delayed)
              // This helps catch first-time social logins that take longer
              const tokenCheckInterval = setInterval(async () => {
                // Only check if we're still loading and haven't resolved yet
                if (!this.isLoading || !this.auth0Resolve) {
                  clearInterval(tokenCheckInterval);
                  return;
                }

                const accessToken = localStorage.getItem('auth0_access_token');
                const idToken = localStorage.getItem('auth0_id_token');

                if (accessToken && idToken) {
                  console.log(
                    '✅ Tokens found via periodic check, completing registration...'
                  );
                  clearInterval(tokenCheckInterval);
                  clearTimeout(loadingTimeout);

                  try {
                    // Complete the registration process
                    const userInfo = await this.mobileAuth.getUserInfo(
                      accessToken
                    );
                    const userEmail = userInfo.email || userInfo.sub || '';
                    const userName =
                      userInfo.name ||
                      userInfo.given_name ||
                      userInfo.nickname ||
                      'User';
                    const roles =
                      userInfo['https://kyrgyz-translation-api/roles'] || [];
                    const permissions =
                      userInfo['https://kyrgyz-translation-api/permissions'] ||
                      [];

                    this.isLoading = false;

                    if (this.auth0Resolve) {
                      this.auth0Resolve({
                        email: userEmail,
                        name: userName,
                        roles: roles,
                        permissions: permissions,
                      });
                    }

                    this.auth0Reject = null;
                    this.auth0Resolve = null;
                    this.pendingAuth0State = null;
                  } catch (error) {
                    console.error(
                      '❌ Error completing registration from periodic check:',
                      error
                    );
                    clearInterval(tokenCheckInterval);
                  }
                }
              }, 2000); // Check every 2 seconds

              // Clear interval when timeout fires
              setTimeout(() => {
                clearInterval(tokenCheckInterval);
              }, 120000);
            })
            .catch((browserError) => {
              clearTimeout(loadingTimeout);
              this.isLoading = false;
              reject(browserError);
            });
        });
      } else {
        // Web: Use standard Auth0 with signup hint
        this.auth0.loginWithRedirect({
          appState: { target: '/' },
          authorizationParams: {
            screen_hint: 'signup',
          },
        });
        clearTimeout(loadingTimeout);
      }
    } catch (error) {
      console.error('❌ Auth0 register error:', error);
      clearTimeout(loadingTimeout);
      this.isLoading = false;
      alert('Auth0 registration failed: ' + (error as Error).message);
      throw error;
    }
  }
}
